import React, { useState } from 'react';
import UISimpleTable, { SimpleTableColumn } from '@/components/shared/UISimpleTable';
import UITenantInfo from '@/components/shared/UITenantInfo';
import UIBadge from '@/components/shared/UIBadge';
import TenantActionButtons from '@/components/shared/TenantActionButtons';
import { TenantListData } from '../TenantsListSection/TenantsListSection';
import { ReactComponent as DotsIcon } from '@/img/dots.svg';
import './TenantsListTable.scss';

export interface SortState {
  column: string | null;
  direction: 'asc' | 'desc' | null;
}

export interface TenantsListTableProps {
  data?: TenantListData[];
  onTenantAction?: (tenantId: string, action: string) => void;
}

const baseClass = 'TenantsListTable';

const mockHandlers = {
  onTenantAction: (tenantId: string, action: string) => console.log('Action:', action, 'for tenant:', tenantId)
};

const TenantsListTable: React.FC<TenantsListTableProps> = ({
                                                             data = [],
                                                             onTenantAction = mockHandlers.onTenantAction
                                                           }) => {
  const [sortState, setSortState] = useState<SortState>({
    column: null,
    direction: null,
  });

  const handleSort = (columnKey: string, direction: 'asc' | 'desc') => {
    setSortState({ column: columnKey, direction });
  };

  const getPaymentBadge = (status: TenantListData['paymentStatus']) => {
    switch (status) {
      case 'paid':
        return <UIBadge variant="success" shape="pill">PAID</UIBadge>;
      case 'debtor':
        return <UIBadge variant="danger" shape="pill">DEBTOR</UIBadge>;
      case 'not-set':
        return <UIBadge variant="warning" shape="pill">NOT SET</UIBadge>;
      default:
        return <UIBadge variant="secondary" shape="pill">UNKNOWN</UIBadge>;
    }
  };

  // Конфігурація стовпців
  const columns: SimpleTableColumn[] = [
    {
      key: 'name',
      label: 'TENANT',
      width: '359px',
      sortable: true,
      sortDirection: sortState.column === 'name' ? sortState.direction : null,
      showDivider: true,
      render: (_, tenant: TenantListData) => {
        const tenantId = typeof tenant.id === 'string' ? parseInt(tenant.id, 10) : tenant.id;

        return (
          <div className={`${baseClass}__tenant-content`}>
            <UITenantInfo
              name={tenant.name}
              email={tenant.email}
              tenantId={tenantId}
              avatar={tenant.avatar || null}
            />
          </div>
        );
      }
    },
    {
      key: 'paymentStatus',
      label: 'PAYMENT',
      width: '121px',
      sortable: true,
      sortDirection: sortState.column === 'paymentStatus' ? sortState.direction : null,
      showDivider: true,
      render: (_, tenant: TenantListData) => (
        <div className={`${baseClass}__payment-status`}>
          {getPaymentBadge(tenant.paymentStatus)}
        </div>
      )
    },
    {
      key: 'property',
      label: 'PROPERTY',
      width: '200px',
      sortable: true,
      sortDirection: sortState.column === 'property' ? sortState.direction : null,
      showDivider: true,
      render: (property) => (
        <div className={`${baseClass}__property-name`}>
          {property}
        </div>
      )
    },
    {
      key: 'unit',
      label: 'NUMBER',
      width: '114px',
      sortable: true,
      sortDirection: sortState.column === 'unit' ? sortState.direction : null,
      showDivider: true,
      render: (unit) => (
        <div className={`${baseClass}__unit-number`}>
          {unit}
        </div>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      width: '174px',
      showDivider: false,
      render: (_, tenant: TenantListData) => (
        <TenantActionButtons
          tenantId={tenant.id}
          onAction={onTenantAction}
        />
      )
    },
    {
      key: 'menu',
      label: 'Menu',
      width: '48px',
      headerContent: <DotsIcon className={`${baseClass}__menu-dots`} />,
      render: () => <DotsIcon className={`${baseClass}__menu-dots`} />
    }
  ];

  return (
    <UISimpleTable
      data={data}
      columns={columns}
      className={baseClass}
      getRowKey={(tenant) => tenant.id}
      onSort={handleSort}
    />
  );
};

export default TenantsListTable;